/**
 * Currency defaults + per-tenant resolver.
 *
 * Drives the ISO-4217 currency code passed to Intl.NumberFormat /
 * toLocaleString({ style: 'currency' }) so money formats in the TENANT's
 * currency rather than a hardcoded 'AUD'.
 *
 * Parallel to src/utils/locale.ts. getCurrencySync() is the analogue of
 * getLocaleSync() — a localStorage-backed accessor for pure helpers + services
 * that lack React context. TenantContext caches the value on load.
 *
 * Scope note: this covers DISPLAY formatting only. The `currency` DATA field on
 * proposal/invoice pricing objects (typed as the literal 'AUD') is a separate
 * concern — widening that is its own task.
 */
import type { TenantConfig } from '@/types/tenant.types';

/** Platform default currency — Techery bills in Australian dollars. */
export const DEFAULT_TENANT_CURRENCY = 'AUD';

const CURRENCY_STORAGE_KEY = 'tenant_currency';

/**
 * Resolve the display currency code for a tenant. Missing tenant or missing
 * `business.currency` returns the platform default. Prefer in React components
 * (reactive via useTenant).
 */
export function getTenantCurrency(
  tenant: TenantConfig | null | undefined,
): string {
  const override = tenant?.business?.currency?.trim();
  return override || DEFAULT_TENANT_CURRENCY;
}

/**
 * Synchronous currency accessor for pure helpers + services without React
 * context — the currency analogue of getTenantIdSync()/getLocaleSync().
 */
export function getCurrencySync(): string {
  if (typeof window === 'undefined' || !window.localStorage) {
    return DEFAULT_TENANT_CURRENCY;
  }
  return (
    window.localStorage.getItem(CURRENCY_STORAGE_KEY) || DEFAULT_TENANT_CURRENCY
  );
}

/**
 * Cache the active tenant's currency to localStorage so getCurrencySync() can
 * read it. Called by TenantContext whenever a tenant config loads/updates.
 */
export function cacheTenantCurrency(
  tenant: TenantConfig | null | undefined,
): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(CURRENCY_STORAGE_KEY, getTenantCurrency(tenant));
}
